import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import copy from "copy-to-clipboard";
import { toast } from "sonner";
import { ArrowRight, Check, ChevronDown, Copy, ExternalLink } from "lucide-react";
import { Button, Collapsible, Tooltip } from "@/components/ui";
import { cn } from "@/lib/utils";
import { Destination, RouteBlocker } from "../useFormRouteBuilder";

/** Clipboard API first; `copy-to-clipboard` covers browsers and contexts without it. */
export async function copyText(text: string) {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    copy(text);
  }
}

/** Copies the route, toasts, and reads "Copied" for two seconds. */
export function useCopyRoute(url: string | null) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(timer.current), []);

  const copyRoute = useCallback(async () => {
    if (!url) return;
    await copyText(url);
    setCopied(true);
    toast.success("Route copied");
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 2000);
  }, [url]);

  return { copied, copyRoute };
}

/** What's still missing, in words, or "Ready to copy". */
export function routeStatusText(blocker: RouteBlocker | null, formTypeLabel: string, destination: Destination) {
  if (!blocker) return "Ready to copy";
  return {
    missing_default: `Set a default ${formTypeLabel} form to finish`,
    missing_form: "Choose a form to finish",
    missing_player: "Choose a player to finish",
    missing_merge_fields:
      destination === "crm" ? "Add your CRM's merge fields to finish" : "Add the merge fields to finish",
    missing_code: "Loading your route code…",
    code_error: "We couldn't load your route codes. Try again",
  }[blocker];
}

export function RouteStatus({
  blocker,
  text,
  onFix,
  className,
}: {
  blocker: RouteBlocker | null;
  text: string;
  onFix?: (blocker: RouteBlocker) => void;
  className?: string;
}) {
  const ready = !blocker;
  const dot = (
    <span className={cn("size-2 shrink-0 rounded-full", ready ? "bg-emerald-500" : "bg-amber-500")} aria-hidden />
  );
  if (blocker && blocker !== "missing_code" && onFix) {
    return (
      <button
        type="button"
        onClick={() => onFix(blocker)}
        className={cn(
          "group flex items-center gap-2 text-sm font-medium text-amber-700 hover:text-amber-900 cursor-pointer",
          className
        )}
        data-testid="route-status"
      >
        {dot}
        <span className="underline decoration-amber-300 underline-offset-2 group-hover:decoration-amber-600">
          {text}
        </span>
        <ArrowRight className="size-3.5" />
      </button>
    );
  }
  return (
    <div
      className={cn(
        "flex items-center gap-2 text-sm font-medium",
        ready ? "text-emerald-700" : "text-amber-700",
        className
      )}
      data-testid="route-status"
    >
      {dot}
      <span>{text}</span>
    </div>
  );
}

/** True for a moment after `value` changes, so the URL can flash when a setting changes it. */
export function useFlashOnChange(value: unknown, durationMs = 700) {
  const [flashing, setFlashing] = useState(false);
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    setFlashing(true);
    const timer = setTimeout(() => setFlashing(false), durationMs);
    return () => clearTimeout(timer);
  }, [value, durationMs]);
  return flashing;
}

/**
 * The URL with each `{{…}}` merge field drawn as a chip, flashing briefly when it changes. Its spans
 * opt out of the global `span { font-family: "Open Sans" }` rule in base.scss so they stay monospaced.
 */
export function RouteUrl({ url, variant = "card" }: { url: string; variant?: "card" | "bar" }) {
  const flashing = useFlashOnChange(url);
  const parts = url.split(/(\{\{.*?\}\})/g).filter(Boolean);
  return (
    <div
      data-testid={variant === "card" ? "route-url" : "sticky-route-url"}
      data-flashing={flashing || undefined}
      className={cn(
        "font-mono text-slate-800 break-all transition-colors duration-500",
        variant === "card"
          ? "rounded-xl border border-slate-200 px-4 py-3.5 text-[13px] leading-7 shadow-xs"
          : "rounded-lg px-2 py-1 -mx-2 text-xs leading-6",
        flashing ? "bg-amber-50" : variant === "card" ? "bg-white" : "bg-transparent"
      )}
    >
      {parts.map((part, index) =>
        /^\{\{.*\}\}$/.test(part) ? (
          <span key={index} className="ignore-base-styling bg-sky-100 text-sky-900 rounded px-1.5 py-0.5">
            {part}
          </span>
        ) : (
          <span key={index} className="ignore-base-styling">
            {part}
          </span>
        )
      )}
    </div>
  );
}

interface Props {
  url: string | null;
  blocker: RouteBlocker | null;
  statusText: string;
  previewUrl: string | null;
  restored: boolean;
  onReset: () => void;
  onFix: (blocker: RouteBlocker) => void;
  mergeFieldsTitle: string;
  mergeFieldsOpen: boolean;
  onMergeFieldsOpenChange: (open: boolean) => void;
  /** The merge-field mapping, folded in under the route. */
  children: React.ReactNode;
}

export const RouteOutput = React.forwardRef<HTMLElement, Props>(function RouteOutput(
  {
    url,
    blocker,
    statusText,
    previewUrl,
    restored,
    onReset,
    onFix,
    mergeFieldsTitle,
    mergeFieldsOpen,
    onMergeFieldsOpenChange,
    children,
  },
  ref
) {
  const { copied, copyRoute } = useCopyRoute(url);
  const ready = !blocker && !!url;

  return (
    <section
      ref={ref}
      aria-label="Your route"
      data-testid="route-output"
      className="bg-primary/[0.03] border border-slate-200/80 border-l-4 border-l-primary rounded-2xl p-6 shadow-sm flex flex-col gap-4"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-lg font-bold text-slate-900">Your route</h3>
        <RouteStatus blocker={blocker} text={statusText} onFix={onFix} />
      </div>
      {url && <RouteUrl url={url} />}
      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={copyRoute} disabled={!ready}>
          {copied ? <Check className="mr-1.5 h-4 w-4" /> : <Copy className="mr-1.5 h-4 w-4" />}
          {copied ? "Copied" : "Copy route"}
        </Button>
        <Tooltip>
          <Tooltip.Trigger
            render={
              <span>
                <Button
                  variant="outline"
                  className="bg-white"
                  disabled={!ready || !previewUrl}
                  onClick={() => previewUrl && window.open(previewUrl, "_blank", "noopener,noreferrer")}
                >
                  <ExternalLink className="mr-1.5 h-4 w-4" />
                  Preview the form
                </Button>
              </span>
            }
          />
          <Tooltip.Content>Opens the form's test link. Nothing is created.</Tooltip.Content>
        </Tooltip>
        {restored && (
          <span className="ml-auto flex items-center gap-1 text-xs text-slate-400">
            Restored your last route ·
            <Button variant="link" size="inline" className="text-xs" onClick={onReset}>
              Start over
            </Button>
          </span>
        )}
      </div>
      <Collapsible
        open={mergeFieldsOpen}
        onOpenChange={onMergeFieldsOpenChange}
        className="border-t border-slate-200/80 pt-3"
      >
        <Collapsible.Trigger className="group flex items-center gap-1.5 text-sm font-semibold text-slate-700 hover:text-slate-900 cursor-pointer">
          <ChevronDown className="size-4 transition-transform -rotate-90 group-data-[panel-open]:rotate-0" />
          {mergeFieldsTitle}
        </Collapsible.Trigger>
        <Collapsible.Content className="mt-3">{children}</Collapsible.Content>
      </Collapsible>
    </section>
  );
});
